<script setup lang="ts">
import FormCard from "~/components/ui/FormCard.vue";
import BaseForm from "~/components/ui/BaseForm.vue";
import ProgressBar from "~/components/ui/ProgressBar.vue";
import Accordion from "~/components/ui/Accordion.vue";
import Checkbox from "~/components/inputs/Checkbox.vue";
import cyberWebyConsent from "~/rules/cyberWebyConsent";
import { useVuelidate } from "@vuelidate/core";
import Validator from "~/utils/validator";

import { CYBERWEBY_PRIVACY_URL, CYBERWEBY_TERMS_URL } from "~/utils/constants";
const locale = await getLocale("forms", "cyberweby");

defineProps({
  progress: {
    type: Number,
    required: true,
  },
});

const consent_cyber_main = useState<boolean | null>("consent_cyber_main");
const consent_cyber1 = useState<boolean | null>("consent_cyber1");
const consent_cyber2 = useState<boolean | null>("consent_cyber2");
const consent_cyber3 = useState<boolean | null>("consent_cyber3");

const mainClicked = useCheckboxAccordion(consent_cyber_main, [consent_cyber1, consent_cyber2, consent_cyber3])

const emit = defineEmits(["init", "submit", "decline"]);

const v$ = useVuelidate(cyberWebyConsent, {
  consent_cyber_main,
  consent_cyber1,
  consent_cyber2,
  consent_cyber3
});

const validate = () => {
  v$.value.$touch();
  v$.value.$validate();
  return !v$.value.$error;
};

const submit = () => {
  if (validate()) {
    emit("submit");
  }
};

onMounted(() => emit("init"));
</script>

<template>
  <div>
    <BaseForm @submit.prevent="submit" class="cyberweby-form">
      <template #upper>
        <ProgressBar :value="progress" />
        <FormCard :title="locale.title" :submit-text="locale.submit" >
          <p
            class="mb-4"
            v-html="locale.paragraph1"
          ></p>
          <p
            class="mb-4"
            v-html="
              locale.paragraph2
                .replace('%d', CYBERWEBY_TERMS_URL)
                .replace('%z', CYBERWEBY_PRIVACY_URL)
            "
          ></p>
          <Accordion>
            <Checkbox
              v-model="consent_cyber_main"
              test-id="consent_cyber_main"
              @click="() => (mainClicked = true)"
              :error="v$.consent_cyber_main.$error"
              :error-message="Validator.message(v$.consent_cyber_main)"
            >
              <div data-testid="consentAccordion" v-html="locale.accordion_text"></div>
            </Checkbox>

            <template #items>
              <div class="pt-4 pl-8">
                <Checkbox 
                  v-model="consent_cyber1" 
                  class="mb-4" 
                  test-id="consent_cyber1"
                  :error="v$.consent_cyber1.$error"
                  :error-message="Validator.message(v$.consent_cyber1)"
                >
                  <div
                    v-html="
                      locale.consents.consent1.replace('%d', CYBERWEBY_TERMS_URL)
                    "
                  ></div>
                </Checkbox>
                <Checkbox 
                  v-model="consent_cyber2" 
                  class="mb-4" 
                  test-id="consent_cyber2"
                  :error="v$.consent_cyber2.$error"
                  :error-message="Validator.message(v$.consent_cyber2)"
                >
                  <div
                    v-html="
                      locale.consents.consent2.replace('%d', CYBERWEBY_TERMS_URL)
                    "
                  ></div>
                </Checkbox>
                <Checkbox 
                  v-model="consent_cyber3" 
                  class="mb-4" 
                  test-id="consent_cyber3"
                  :error="v$.consent_cyber3.$error"
                  :error-message="Validator.message(v$.consent_cyber3)"
                >
                  <div
                    v-html="locale.consents.consent3.replace('%d', CYBERWEBY_PRIVACY_URL)"
                  ></div>
                </Checkbox>
              </div>
            </template>
          </Accordion>
          <template #link>
            <div class="text-center text-gray-700 mt-4 mb-8">
              <button class="underline" @click="emit('decline')">
                {{ locale.decline }}
              </button>
            </div>
          </template>
        </FormCard>
      </template>
    </BaseForm>
  </div>
</template>

<style>
  .cyberweby-form .small-checkbox.error label {
    padding-bottom: 0.1em !important;
  }
  .cyberweby-form .small-checkbox label > div {
    @apply text-xs;
  }
  .cyberweby-form .small-checkbox.error label > div {
    @apply pb-0.5;
  }
  .cyberweby-form .small-checkbox label > div[data-testid=consentAccordion] {
    @apply text-sm;
  }
</style>
